import React, { ReactNode, useContext, useState } from 'react'
import styled from 'styled-components'

const StyledForm = styled.form`
  fieldset:last-of-type section {
    border-bottom: 1px solid ${props => props.theme.colors.separator};
  }

  margin-bottom: ${props => props.theme.spacing.base700};
`

export type ValidationErrors = {
  [inputName: string]: string
}

const formContext = React.createContext<{
  validationErrors: ValidationErrors
  setValidationErrors: (validationErrors: ValidationErrors) => void
}>({
  validationErrors: {},
  setValidationErrors: () => {},
})

export const useFormValidator = () => {
  const { validationErrors, setValidationErrors } = useContext(formContext)

  const putError = (inputName: string, error: string) => {
    if (!(inputName in validationErrors) || validationErrors[inputName] !== error) {
      setValidationErrors({
        ...validationErrors,
        [inputName]: error,
      })
    }
  }

  const removeError = (inputName: string) => {
    if (inputName in validationErrors) {
      const newValidationErrors = { ...validationErrors }
      delete newValidationErrors[inputName]
      setValidationErrors(newValidationErrors)
    }
  }

  const getError = (inputName: string) => {
    return validationErrors[inputName]
  }

  return {
    putError,
    removeError,
    getError,
  }
}

const Form = ({
  children,
  ...props
}: {
  children: ({
    isValid,
    validationErrors,
  }: {
    isValid: boolean
    validationErrors: ValidationErrors
  }) => JSX.Element
} & Omit<React.FormHTMLAttributes<HTMLFormElement>, 'children'>) => {
  const initialValues = useContext(formContext)
  const [validationErrors, setValidationErrors] = useState(initialValues.validationErrors)

  const context = {
    validationErrors,
    setValidationErrors,
  }

  const isValid = Object.entries(validationErrors).length === 0

  return (
    <formContext.Provider value={context}>
      <StyledForm {...props}>{children({ isValid, validationErrors })}</StyledForm>
    </formContext.Provider>
  )
}

export default Form
